import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { platformName, platformOrder } from '../../../platformMeta';
import { ChannelMark } from '../rollout/ChannelMark';
import type { PlanStepView } from '../rollout/rolloutModel';
import { rolloutMessages } from '../rollout/rolloutMessages';
import { reelsPlanMessages } from './reelsPlanMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import rolloutStyles from '../rollout/Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanChannelsLineProps {
  steps: PlanStepView[];
  /** The scheduled posts of this month, on a plan with a limit: "June: 46 of 200 scheduled posts used". */
  quota: { month: string; used: number; limit: number } | null;
}

/**
 * The channels line of the run sheet of an AI reels plan (design rollout-plan f3 `.chline`): the
 * channels that the steps post on, "Drafts appear when a reel is ready." and the month count. A
 * step changes its own channels in its sheet.
 */
const ReelsPlanChannelsLineComponent: React.FC<ReelsPlanChannelsLineProps> = ({ steps, quota }) => {
  const intl = useIntl();
  const platforms = useMemo(() => {
    const used = new Set(steps.flatMap((step) => step.channels.map((channel) => channel.platform)));
    return platformOrder.filter((platform) => used.has(platform));
  }, [steps]);

  return (
    <div className={rolloutStyles.chline}>
      <span className={rolloutStyles.chns}>
        {platforms.map((platform) => (
          <ChannelMark key={platform} platform={platform} small />
        ))}
      </span>
      <span>
        {platforms.length > 0 && <b>{platforms.map((platform) => platformName(platform)).join(', ')}.</b>}{' '}
        {intl.formatMessage(reelsPlanMessages.channelsLine)}
      </span>
      <span className={rolloutStyles.grow} />
      {quota !== null && (
        <span className={rolloutStyles.cost}>{intl.formatMessage(rolloutMessages.channelsQuota, quota)}</span>
      )}
    </div>
  );
};

export const ReelsPlanChannelsLine = TypedMemo(ReelsPlanChannelsLineComponent);
